import { ADMIN_LOGIN_PATH, ADMIN_PASSWORD_PATH } from '@/services/adminSession';
import { currentRealm, REALM } from '@/services/realm';
import type { Realm } from '@/services/types/Realm';

// The single place that sends the browser back to MPass, plus the loop guard that stops a
// refused account from bouncing between the SPA and the login gate forever.
//
// Why this lives outside React: RequireSession (a component) and the 401 handler installed by
// SessionProvider (a callback registered into services/api.ts) both need to trigger the same
// navigation, and api.ts must stay React-free. Both call redirectToLogin().
//
// Why a stash instead of a return parameter: /saml/authenticate/mpass accepts NO return target
// on either path. Under mock-gov, MockAuthController.mockLoginPage's signature is
// (@PathVariable registrationId, Model) — RelayState/returnTo aren't ignored, they're invisible;
// verified live, the responses with and without them are byte-identical. On the real SAML path
// MPassSamlConfig's SamlEntryRedirect returns "redirect:/saml2/authenticate/{id}" and drops the
// query string, and its successHandler ends in an unconditional response.sendRedirect("/app/")
// which also replaces Spring's SavedRequestAwareAuthenticationSuccessHandler. So the client has
// to remember the path itself. Sending a parameter the backend cannot read would be theatre.

/** Where re-authentication starts. Same target RequireSession has always used. */
export const LOGIN_URL = '/saml/authenticate/mpass';

/**
 * Where a signed-out visitor goes to sign in: MPass on the public site, the administration's own
 * password screen on its host (ADM-1, services/realm.ts).
 */
export function loginUrl(realm: Realm): string {
  return realm === REALM.admin ? ADMIN_LOGIN_PATH : LOGIN_URL;
}

/**
 * Where we go instead when re-authenticating clearly is not fixing anything. With no `reason`
 * param, features/auth/MpassErrorPage shows its generic "Autentificare anulată sau eșuată" card,
 * whose retry link is a human click and therefore cannot spin.
 *
 * A full-page assignment rather than a router navigation, deliberately: this path exists because
 * the session bootstrap is misbehaving, and a hard load is the one way to be sure nothing stale
 * survives into the screen that has to be trustworthy. That the screen is now React rather than a
 * Thymeleaf template does not change that — it is still reached by leaving this document.
 */
export const GIVE_UP_URL = '/auth/mpass-error';

const STAMP_KEY = 'akis.reauth-at';
const RETURN_TO_KEY = 'akis.return-to';

/**
 * Is this path one of the two MPass screens?
 *
 * Both are React routes now, which means the SPA boots on them — something that could not happen
 * while they were Thymeleaf pages. Anything that runs at boot and touches the re-authentication
 * state has to exclude them, or it acts in the middle of the round trip it is meant to bracket.
 *
 * Matched by prefix because the sign-in route carries a registration id
 * (/saml/authenticate/{registrationId}); LOGIN_URL is just the mpass one.
 */
export function isAuthScreen(pathname: string): boolean {
  return (
    pathname.startsWith('/saml/authenticate/') ||
    pathname === GIVE_UP_URL ||
    pathname === ADMIN_LOGIN_PATH ||
    pathname === ADMIN_PASSWORD_PATH
  );
}

/**
 * Two redirects to MPass inside this window mean the round trip completed and we came back
 * still unauthenticated — re-authenticating again would just spin. Deliberately shorter than
 * any real session lifetime and longer than a full MPass round trip.
 */
export const REAUTH_WINDOW_MS = 10_000;

export type ReauthDecision = { action: 'login' } | { action: 'give-up' };

/**
 * The whole loop policy, as a pure function so it is testable without a DOM.
 *
 * A genuine second expiry an hour later is `now - lastAttempt` ≈ 3_600_000, far outside the
 * window, so it redirects exactly like a first expiry. Only a *second* redirect within
 * REAUTH_WINDOW_MS is suppressed.
 */
export function decideReauth(now: number, lastAttempt: number | null): ReauthDecision {
  if (lastAttempt !== null && lastAttempt > 0 && now - lastAttempt < REAUTH_WINDOW_MS) {
    return { action: 'give-up' };
  }
  return { action: 'login' };
}

/**
 * What a mid-session 401 should do: leave now, raise the panel, or nothing at all.
 *
 * 'ignore' is the case that is easy to miss. A dead session answers 401 to every request that
 * follows, not one per user action, and five mutation handlers reload their list after the
 * catch — so the reload runs even when the mutation failed and takes its own 401 moments
 * later. Read as a fresh GET it would navigate away from the panel the mutation just raised,
 * discarding exactly the unsaved work the panel exists to protect. The first 401 decides; the
 * rest are the same expiry arriving again.
 *
 * Pure, and separate from SessionProvider, for the same reason decideReauth is: this runs in a
 * callback registered into api.ts, and the tests have no DOM to render a provider into.
 */
export type ExpiryAction = 'redirect' | 'announce' | 'ignore';

export function decideOn401(method: string, alreadyAnnounced: boolean): ExpiryAction {
  if (alreadyAnnounced) return 'ignore';
  // Nothing is being typed into a read, and the screen is already broken, so go now.
  if (method.toUpperCase() === 'GET') return 'redirect';
  // A mutation: surface it and let the user choose when to leave.
  return 'announce';
}

/**
 * Guards what we hand to history.replaceState after the login round trip. The value comes from
 * our own sessionStorage, but stored input is still input, and replaceState to an
 * attacker-chosen path is a cheap own-goal. Only the path portion is checked: replaceState
 * resolves against the current origin, so a query string can hold whatever it likes.
 */
export function isRestorablePath(candidate: string | null | undefined): candidate is string {
  if (!candidate) return false;
  const path = candidate.split('?')[0];
  if (!path.startsWith('/app/')) return false;
  return !path.includes('//') && !path.includes('\\') && !path.includes(':');
}

// sessionStorage (not localStorage) because the stamp has to survive a full-page navigation and
// the MPass round trip — same tab, and both login paths land back on this origin at /app/ — but
// must NOT be shared between tabs, or tab A's redirect would suppress tab B's legitimate one.
// It throws outright in some privacy configurations, hence the try/catch on every access.
function readStore(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStore(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Fail open: the redirect is the feature, the guard is only the net.
  }
}

function removeStore(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Same reasoning as writeStore.
  }
}

/** Called after a successful /api/v1/me — the round trip worked, so the next expiry starts clean. */
export function clearReauthStamp(): void {
  if (typeof window === 'undefined') return;
  removeStore(STAMP_KEY);
}

// One navigation per page lifetime. RequireSession assigns the redirect from its render body, so
// a re-render (StrictMode's double render, a route change while the navigation is still in
// flight) would otherwise write a second stamp and trip the guard against itself.
let navigating = false;

/**
 * Send the browser to MPass, remembering where we were — or, if we already tried moments ago,
 * to the readable refusal screen instead of round-tripping again.
 */
export function redirectToLogin(): void {
  if (typeof window === 'undefined' || navigating) return;
  navigating = true;

  const decision = decideReauth(Date.now(), Number(readStore(STAMP_KEY)) || null);
  if (decision.action === 'give-up') {
    // Clear on the way out so the error page's "try again" link gets a clean first attempt
    // rather than an immediately re-tripped guard.
    removeStore(STAMP_KEY);
    removeStore(RETURN_TO_KEY);
    window.location.href = GIVE_UP_URL;
    return;
  }

  writeStore(STAMP_KEY, String(Date.now()));
  writeStore(RETURN_TO_KEY, window.location.pathname + window.location.search);
  window.location.href = loginUrl(currentRealm());
}

/**
 * Put the browser back on the page the user was on before re-authenticating.
 *
 * Called from main.tsx at module scope, BEFORE createRoot().render(), so createBrowserRouter
 * boots directly on the restored path: no cabinet flash, no extra history entry. Doing it in an
 * effect would be too late — <Navigate to="/app/cabinet" replace/> has already resolved by then.
 *
 * Restores the PATH, not the page state. Anything typed is gone the moment window.location
 * changes; that is what the non-navigating mutation branch in session.tsx exists to protect.
 */
export function restoreReturnTo(): void {
  if (typeof window === 'undefined') return;
  // The sign-in screens are SPA routes now (features/auth/), so this module — which runs on every
  // SPA boot — runs on them too. It did not before: they were Thymeleaf pages, and the stash
  // survived the whole round trip because the bundle never loaded during it. Reading it here would
  // consume it one page BEFORE the login that needs it, and the user would always land on the
  // cabinet root. Leave it alone until the browser is somewhere the restore could apply.
  if (isAuthScreen(window.location.pathname)) return;
  const target = readStore(RETURN_TO_KEY);
  removeStore(RETURN_TO_KEY); // one-shot, whether or not we use it
  if (!isRestorablePath(target)) return;
  // Both login paths hard-code /app/ as the landing, so that is the only place a restore is
  // ever correct. Anywhere else the user navigated themselves and we must not fight them.
  if (window.location.pathname !== '/app/') return;
  window.history.replaceState(null, '', target);
}

/**
 * The page to land on after the administration's password sign-in: the one the user was sent away
 * from, when it is a cabinet page, or null. One-shot, as restoreReturnTo is; the password screen
 * is an SPA route, so it hands the path to a navigation instead of rewriting /app/ before boot.
 */
export function consumeReturnTo(): string | null {
  if (typeof window === 'undefined') return null;
  const target = readStore(RETURN_TO_KEY);
  removeStore(RETURN_TO_KEY);
  return isRestorablePath(target) ? target : null;
}
